import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { SelectField } from '../form-controls';
import { CommitmentFields } from './commitment-fields';
import { PriceRangesList } from './price-ranges-list';
import { TemplateSection, TemplateStack } from './template-shell';
import type { ContractFormValues } from '../contract-form';

const PRICE_METHOD_OPTIONS = [
  { value: 'tierd', label: 'Tiered' },
  { value: 'progressive', label: 'Progressive' },
];

/**
 * Config.Bandwidth template (`service_config.type = 'bandwidth'`). Seeds a single
 * bandwidth config option (name `global`) with a commitment block, a comparison
 * method (tiered/progressive) and a tiered price-range list. Writes the option
 * into `products.{i}.service_configoptions[0]`.
 */
export function ConfigBandwidthTemplate({ index }: { index: number }) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;
  const options = useWatch({ control, name: optsPath });
  const optPath = `${optsPath}.0` as const;

  const commitmentValue = Number(
    useWatch({ control, name: `${optPath}.commitment_value` as never }) ?? 0,
  );

  // Seed the single bandwidth option once (legacy defaults a `global` scope).
  useEffect(() => {
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length > 0) return;
    setValue(
      optsPath,
      [
        {
          name: 'global',
          type: 'bandwidth',
          unit: 'mbps',
          commitment_value: 0,
          commitment_price_per_unit: 0,
          commitment_time: 1,
          commitment_prepaid: true,
          price_config: { method: 'tierd' },
          prices: [{ price_start: 0, price_end: 999999999, price_per_unit: 0 }],
        },
      ],
      { shouldDirty: true },
    );
  }, [optsPath, getValues, setValue]);

  if (!Array.isArray(options) || options.length === 0) return null;

  return (
    <TemplateStack>
      <TemplateSection title="Bandwidth commitment">
        <CommitmentFields optPath={optPath} />
        <SelectField
          name={`${optPath}.price_config.method` as never}
          label="Form of comparison"
          options={PRICE_METHOD_OPTIONS}
          required
        />
      </TemplateSection>
      <TemplateSection title="Pricing configuration">
        <PriceRangesList name={`${optPath}.prices`} commitmentValue={commitmentValue} />
      </TemplateSection>
    </TemplateStack>
  );
}
